import * as React from "react";

export function useCopyToClipboard(duration = 1500) {
  const [copiedText, setCopiedText] = React.useState<string | null>(null);
  const [isCopying, setIsCopying] = React.useState(false);
  const [hasCopied, setHasCopied] = React.useState(false);

  const copy = React.useCallback(async (text: string) => {
    if (!navigator?.clipboard) {
      // eslint-disable-next-line no-console
      console.warn("Clipboard not supported");
      setCopiedText(null);
      setIsCopying(false);

      return false;
    }

    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setHasCopied(true);

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Copy failed: ", error);
      setCopiedText(null);
      setIsCopying(false);

      return false;
    }
  }, []);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (hasCopied) {
      timeout = setTimeout(() => {
        setHasCopied(false);
        setIsCopying(false);
      }, duration);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasCopied, duration]);

  return React.useMemo(() => {
    return {
      /** Function to copy text to clipboard */
      copy,
      /** Indicates if the copy operation is in progress. Use to show a loading indicator or disable the copy button */
      isCopying,
      /** Indicates if the text has been copied. Use to display a temporary message or indicator */
      hasCopied,
      /** The text that was copied. */
      copiedText,
    };
  }, [copy, isCopying, hasCopied, copiedText]);
}
